import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '#pplx/frontend/aether/components/Badge/index.js';
import { ContentItemInternal } from '#pplx/frontend/aether/components/ContentItem/index.js';
import { useMenuContext } from '#pplx/frontend/aether/components/internal/menu/useMenuContext.js';
import { useMenuItemRegistration } from '#pplx/frontend/aether/components/internal/menu/useMenuItemRegistry.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import classNames from 'classnames';
import { useCallback, useRef } from 'react';
const VALID_MENU_ITEM_ROLES = new Set(['menuitem', 'menuitemcheckbox', 'menuitemradio']);
/**
 * A base component for menu items. This component does not provide any
 * interactive functionality and is meant to be extended by other menu item
 * components.
 *
 * Aether-internal: this is meant to be used by the Menu component only.
 */
export function MenuItemBase({ ref, children, variant = 'default', leadingAccessory, trailingAccessory, subtitle, textOverflow, role = 'menuitem', badge, badgeVariant = 'info', 
// We intentionally capture the rest of props that could be cloned/passed in by 3rd-party libraries
// such as Radix UI for their own internal usage such as data-state, onFocus, onBlur, etc.
...props }) {
    // Create internal ref for focus functionality
    const internalRef = useRef(null);
    // Create stable focus callback for menu item registration
    const focus = useCallback(() => {
        internalRef.current?.focus();
    }, []);
    // Register this menu item with the menu context
    useMenuItemRegistration({ focus });
    // Combine internal ref with external ref
    const combinedRef = useCallback((element) => {
        // Set internal ref (HTMLDivElement is a subtype of HTMLElement)
        internalRef.current = element;
        // Forward to external ref if provided
        if (!ref)
            return;
        if (typeof ref === 'function') {
            ref(element);
        }
        else {
            ref.current = element;
        }
    }, [ref]);
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to the div.
    const __radixProps = useRadixProps(props);
    // Radix UI uses the presence of the data-disabled attribute (with an empty string or "true" value)
    // to indicate that the item is disabled.
    // In mobile mode (dialog), we use aria-disabled instead.
    const disabled = __radixProps['data-disabled'] === '' ||
        __radixProps['data-disabled'] === 'true' ||
        props['aria-disabled'] === true ||
        props['aria-disabled'] === 'true';
    // Radix UI uses the presence of the data-highlighted attribute (with an empty string value or "true" value)
    // to indicate that the item is highlighted (i.e: selected).
    // For SubTrigger, Radix sets data-state="open" when the submenu is open.
    const highlighted = __radixProps['data-highlighted'] === '' ||
        __radixProps['data-highlighted'] === 'true' ||
        __radixProps['data-state'] === 'open';
    // We show the focus ring only when the role isn't one of the standard menuitem roles
    const shouldShowFocusRing = !VALID_MENU_ITEM_ROLES.has(role);
    const menuContext = useMenuContext();
    const isMobileBottomSheet = Boolean(menuContext?.isMobileBottomSheet);
    const hasWidthConstraint = menuContext?.hasWidthConstraint;
    return (_jsx(ContentItemInternal, { ref: combinedRef, role: role, disabled: disabled, highlighted: highlighted, shouldShowFocusRing: shouldShowFocusRing, leadingAccessory: leadingAccessory, trailingAccessory: trailingAccessory, subtitle: subtitle, textOverflow: textOverflow, variant: variant, rounded: true, size: isMobileBottomSheet ? 'large' : 'default', className: classNames(props.className, hasWidthConstraint
            ? 'max-w-full'
            : 'min-w-[calc(var(--radix-dropdown-menu-trigger-width)-theme(spacing.2))]'), ...__radixProps, children: _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: classNames('min-w-0', !badge && 'flex-1', textOverflow === 'ellipsis' && 'truncate'), children: children }), badge && _jsx(Badge, { variant: badgeVariant, children: badge })] }) }));
}
//# sourceMappingURL=MenuItemBase.js.map